import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Share } from 'react-native';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  type DocumentData,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QuerySnapshot,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import { CURATED_EVENTS, readFirestoreActivities } from './useFeed';
import type { OrganizerSummary, TonyteActivity, VenueSummary } from '../types';
import { isActivityCurrentOrUpcoming, parseActivityDate, sortActivitiesByStart } from '../utils/activityDates';
import { getResolvedEventId } from '../utils/eventIds';
import { buildTonyteEventLink, buildTonyteOrganizerLink, buildTonyteVenueLink } from '../utils/shareLinks';
import { loadCurrentTonyteUser, shareEventExternally } from '../utils/activityActions';
import { translateCurrent, useI18n } from '../i18n';
import { assertSocialWriteAllowed } from '../utils/verificationGate';

// Venue categories — the Vibe at Venues filter chips and the only values a
// partner may pick for their venue (mirrored in firestore.rules).
export const VENUE_CATEGORIES = ['Clubs', 'Bars', 'Cafés', 'Community', 'Student Organisations', 'Student', 'Live Music', 'DJ', 'Musician', 'Culture', 'Food', 'Outdoor'] as const;

const slugify = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

export const venueKeyForActivity = (activity: TonyteActivity) => {
  // A partner's events belong to that partner's venue (venues/{slug(partnerId)}),
  // wherever the individual event's location text points.
  if (activity.partnerId) return slugify(activity.partnerId);
  const label = firstString(activity.venueId, activity.venueName, activity.clubName, activity.locationName, activity.hostName);
  return slugify(label || activity.id);
};

const organizerKeyForActivity = (activity: TonyteActivity) => {
  const label = firstString(
    activity.organizerId,
    activity.primaryHostType === 'organizer' ? activity.primaryHostId : '',
    activity.createdBy && activity.createdBy !== 'system' ? activity.createdBy : '',
    activity.hostId && activity.hostId !== 'system' ? activity.hostId : '',
  );
  return slugify(label || firstString(activity.organizerName, activity.hostName, activity.createdBy, activity.id));
};

const venueNameForActivity = (activity: TonyteActivity) =>
  firstString(activity.venueName, activity.clubName, activity.locationName, activity.hostName, activity.city, 'Tonyte Venue');

const usernameForVenue = (activity: TonyteActivity, venueName: string) =>
  firstString(activity.venueUsername, slugify(venueName).replace(/-/g, '.'));

const categoryForVenue = (events: TonyteActivity[]) => {
  const text = events.map(event => [
    event.category,
    event.title,
    event.description,
    event.venueName,
    event.clubName,
    ...(event.tags || []),
    ...(event.hashtags || []),
  ].join(' ').toLowerCase()).join(' ');
  if (/club|techno|rave|dj|dance|nightlife/.test(text)) return 'Clubs';
  if (/bar|cocktail|drinks|pub/.test(text)) return 'Bars';
  if (/cafe|café|coffee/.test(text)) return 'Cafés';
  if (/student|campus|erasmus|esn/.test(text)) return 'Student';
  if (/concert|live music|band|jazz|open mic|music/.test(text)) return 'Live Music';
  if (/museum|gallery|theatre|art|culture|film/.test(text)) return 'Culture';
  if (/food|restaurant|dinner|brunch|tasting/.test(text)) return 'Food';
  if (/park|outdoor|walk|river|garden/.test(text)) return 'Outdoor';
  return events[0]?.category === 'food' ? 'Food' : events[0]?.category === 'nightlife' ? 'Clubs' : 'Culture';
};

type VenueProfileSnapshot = Partial<VenueSummary> & {
  fallbackFollowersCount?: number;
  partnerId?: string;
};

export function buildVenueSummaries(events: TonyteActivity[], venueProfiles: Record<string, VenueProfileSnapshot> = {}): VenueSummary[] {
  const byVenue = new Map<string, TonyteActivity[]>();
  events.forEach(event => {
    const key = venueKeyForActivity(event);
    if (!byVenue.has(key)) byVenue.set(key, []);
    byVenue.get(key)!.push(event);
  });

  return Array.from(byVenue.entries()).map(([id, venueEvents]) => {
    const sorted = [...venueEvents].sort(sortActivitiesByStart);
    const primary = sorted.find(event => isActivityCurrentOrUpcoming(event)) || sorted[0];
    const name = venueNameForActivity(primary);
    const profile = venueProfiles[id] || {};
    const tags = unique(sorted.flatMap(event => [
      event.category,
      ...(event.tags || []),
      ...(event.hashtags || []),
      ...(event.musicGenres || []),
    ].map(tag => String(tag || '').trim()))).slice(0, 10);
    const upcomingCount = sorted.filter(event => isActivityCurrentOrUpcoming(event)).length;
    const pastCount = sorted.length - upcomingCount;
    const profileFollowersCount = typeof profile.followersCount === 'number' ? profile.followersCount : undefined;
    const fallbackFollowersCount = typeof profile.fallbackFollowersCount === 'number' ? profile.fallbackFollowersCount : undefined;
    return {
      id,
      name: firstString(profile.name, name),
      username: firstString(profile.username, usernameForVenue(primary, name)),
      logoUrl: firstString(profile.logoUrl, primary.venueLogoUrl, primary.photoUrl, primary.thumbnailUrl),
      coverUrl: firstString(profile.coverUrl, primary.coverPhoto, primary.coverImageUrl, primary.imageUrl, primary.posterUrl, primary.photoUrl),
      category: firstString(profile.category, categoryForVenue(sorted)),
      city: firstString(profile.city, primary.city, 'Dresden'),
      address: firstString(profile.address, primary.address, primary.location, primary.locationName),
      bio: firstString(profile.bio, `Venue vibes from ${name}. Follow for upcoming nights, culture drops and posts from Tonyte.`),
      tags: unique([...(Array.isArray(profile.tags) ? profile.tags : []), ...tags]).slice(0, 10),
      followersCount: profileFollowersCount ?? fallbackFollowersCount ?? 0,
      fallbackFollowersCount,
      postsCount: sorted.length,
      upcomingCount,
      pastCount,
      representativeEventId: getResolvedEventId(primary),
      coordinates: primary.coordinates,
      ownerUid: profile.ownerUid,
      adminOnly: profile.adminOnly,
    };
  }).sort((a, b) => b.upcomingCount - a.upcomingCount || b.postsCount - a.postsCount || a.name.localeCompare(b.name));
}

// A partner venue's events: those keyed to it (partnerId -> venue id, or an
// approved collaboration) plus every event its owner account owns. Used by the
// venue list, the venue profile and the analytics dashboard so all three
// always count the same events.
// Only `activities` is trusted here: its rules pin ownerUid/hostId to the
// creator and reserve partnerId for admins/the server, so nobody can attach
// an event to someone else's venue. (officialEvents/events are staff-written
// collections with no such ownership guarantees.)
export function partnerVenueEvents(events: TonyteActivity[], venueId: string, ownerUid?: string) {
  const trusted = events.filter(event => event.sourceCollection === 'activities');
  const keyed = new Set(filterVenueEvents(trusted, venueId).map(event => getResolvedEventId(event)));
  return trusted.filter(event => keyed.has(getResolvedEventId(event)) || (Boolean(ownerUid) && event.ownerUid === ownerUid));
}

// Venues backed by a real partner account: every venues/{id} doc carrying a
// partnerId, with counts from that venue's public events.
export function buildPartnerVenueSummaries(events: TonyteActivity[], venueProfiles: Record<string, VenueProfileSnapshot>): VenueSummary[] {
  return Object.entries(venueProfiles)
    .filter(([, profile]) => Boolean(profile.partnerId))
    .map(([id, profile]) => {
      const venueEvents = [...partnerVenueEvents(events, id, profile.ownerUid)].sort(sortActivitiesByStart);
      const upcomingCount = venueEvents.filter(event => isActivityCurrentOrUpcoming(event)).length;
      const primary = venueEvents.find(event => isActivityCurrentOrUpcoming(event)) || venueEvents[0];
      return {
        id,
        name: firstString(profile.name, 'Tonyte Venue'),
        username: firstString(profile.username, id),
        logoUrl: profile.logoUrl,
        coverUrl: profile.coverUrl,
        category: firstString(profile.category, venueEvents.length ? categoryForVenue(venueEvents) : '', 'Culture'),
        city: firstString(profile.city, primary?.city, 'Dresden'),
        address: firstString(profile.address),
        bio: firstString(profile.bio),
        tags: Array.isArray(profile.tags) ? profile.tags : [],
        followersCount: profile.followersCount ?? profile.fallbackFollowersCount ?? 0,
        fallbackFollowersCount: profile.fallbackFollowersCount,
        postsCount: venueEvents.length,
        upcomingCount,
        pastCount: venueEvents.length - upcomingCount,
        representativeEventId: primary ? getResolvedEventId(primary) : undefined,
        coordinates: profile.coordinates ?? primary?.coordinates ?? null,
        ownerUid: profile.ownerUid,
        adminOnly: profile.adminOnly,
      };
    })
    .sort((a, b) => b.upcomingCount - a.upcomingCount || a.name.localeCompare(b.name));
}

export function filterVenueEvents(events: TonyteActivity[], venueId: string) {
  const profileKey = `venue_${venueId}`;
  return events.filter(event => {
    if (venueKeyForActivity(event) === venueId) return true;
    if (event.collaboratorProfileKeys?.includes(profileKey)) return true;
    return (event.collaborators || []).some(item => item.type === 'venue' && slugify(item.id) === venueId && item.approved === true);
  });
}

const organizerNameForActivity = (activity: TonyteActivity) =>
  firstString(activity.organizerName, activity.primaryHostType === 'organizer' ? activity.primaryHostName : '', activity.hostName, 'Tonyte Organizer');

const organizerUsernameForActivity = (activity: TonyteActivity, displayName: string) =>
  firstString(activity.organizerUsername, slugify(displayName).replace(/-/g, '.'));

export function buildOrganizerSummaries(events: TonyteActivity[]): OrganizerSummary[] {
  const byOrganizer = new Map<string, TonyteActivity[]>();
  events.forEach(event => {
    const key = organizerKeyForActivity(event);
    if (!key) return;
    if (!byOrganizer.has(key)) byOrganizer.set(key, []);
    byOrganizer.get(key)!.push(event);
  });

  return Array.from(byOrganizer.entries()).map(([id, organizerEvents]) => {
    const sorted = [...organizerEvents].sort(sortActivitiesByStart);
    const primary = sorted.find(event => isActivityCurrentOrUpcoming(event)) || sorted[0];
    const displayName = organizerNameForActivity(primary);
    const tags = unique(sorted.flatMap(event => [
      event.category,
      ...(event.tags || []),
      ...(event.hashtags || []),
      ...(event.musicGenres || []),
    ].map(tag => String(tag || '').trim()))).slice(0, 10);
    const upcomingCount = sorted.filter(event => isActivityCurrentOrUpcoming(event)).length;
    const pastCount = sorted.length - upcomingCount;
    return {
      id,
      profileType: 'organizer' as const,
      displayName,
      username: organizerUsernameForActivity(primary, displayName),
      avatarUrl: firstString(primary.organizerLogoUrl, primary.primaryHostType === 'organizer' ? primary.primaryHostAvatarUrl : '', primary.photoUrl, primary.thumbnailUrl),
      coverImage: firstString(primary.coverPhoto, primary.coverImageUrl, primary.imageUrl, primary.posterUrl, primary.photoUrl),
      bio: `Events and nights hosted by ${displayName}. Follow for upcoming drops on Tonyte.`,
      city: firstString(primary.city, 'Dresden'),
      category: categoryForVenue(sorted),
      verified: true,
      followersCount: 0,
      postsCount: sorted.length,
      eventsCount: sorted.length,
      upcomingCount,
      pastCount,
      tags,
      ownerId: firstString(primary.createdBy, primary.hostId),
    };
  }).sort((a, b) => b.upcomingCount - a.upcomingCount || b.postsCount - a.postsCount || a.displayName.localeCompare(b.displayName));
}

export function filterOrganizerEvents(events: TonyteActivity[], organizerId: string) {
  const profileKey = `organizer_${organizerId}`;
  return events.filter(event => {
    if (organizerKeyForActivity(event) === organizerId) return true;
    if (event.collaboratorProfileKeys?.includes(profileKey)) return true;
    return (event.collaborators || []).some(item => item.type === 'organizer' && slugify(item.id) === organizerId && item.approved === true);
  });
}

export function getCollabLabel(event: TonyteActivity) {
  const organizer = firstString(event.organizerName, event.primaryHostType === 'organizer' ? event.primaryHostName : '', event.hostName);
  const venue = firstString(event.venueName, event.primaryHostType === 'venue' ? event.primaryHostName : '', event.locationName);
  if (organizer && venue && organizer.toLowerCase() !== venue.toLowerCase()) return `${organizer} x ${venue}`;
  return organizer || venue || event.hostName || 'Tonyte';
}

export function searchVenues(venues: VenueSummary[], rawTerm: string, category: string) {
  const term = rawTerm.trim().toLowerCase();
  return venues.filter(venue => {
    const categoryMatches = category === 'All' || venue.category === category || venue.tags.some(tag => tag.toLowerCase() === category.toLowerCase());
    if (!categoryMatches) return false;
    if (!term) return true;
    const haystack = [
      venue.name,
      venue.username,
      venue.category,
      venue.city,
      venue.address,
      venue.bio,
      ...venue.tags,
    ].join(' ').toLowerCase();
    return haystack.includes(term);
  });
}

// Module-level cache (mirrors useFeed.ts's feedCaches pattern): VenueProfileScreen
// and OrganizerProfileScreen can both be mounted at once and previously each ran
// their own readFirestoreActivities() + full `venues` collection read independently.
// Sharing one cache means only the first mount actually fetches.
type VenueVibesCacheState = {
  events: TonyteActivity[];
  venueProfiles: Record<string, VenueProfileSnapshot>;
  loading: boolean;
  error: string | null;
  loaded: boolean;
};

const venueVibesListeners = new Set<(state: VenueVibesCacheState) => void>();
let venueVibesCache: VenueVibesCacheState = {
  events: [],
  venueProfiles: {},
  loading: false,
  error: null,
  loaded: false,
};
let venueVibesLoadPromise: Promise<void> | null = null;
let unsubscribeVenueProfiles: (() => void) | null = null;

// Venue docs change after the first load (followers, logo/cover edits), and
// the collection is small, so keep it live while any venue screen is mounted —
// otherwise the Vibe at Venues list shows a stale followersCount next to a
// VenueProfile screen that reads the same doc live.
function watchVenueProfiles() {
  if (unsubscribeVenueProfiles) return;
  unsubscribeVenueProfiles = onSnapshot(
    collection(firestore, 'venues'),
    snapshot => setVenueVibesCache({ venueProfiles: mapVenueSnapshotsToProfiles(snapshot) }),
    error => console.warn('[Tonyte][RN][VenueVibes] venues listener failed', error),
  );
}

function stopWatchingVenueProfiles() {
  if (venueVibesListeners.size > 0 || !unsubscribeVenueProfiles) return;
  unsubscribeVenueProfiles();
  unsubscribeVenueProfiles = null;
}

function notifyVenueVibesListeners() {
  venueVibesListeners.forEach(listener => listener(venueVibesCache));
}

function setVenueVibesCache(next: Partial<VenueVibesCacheState>) {
  venueVibesCache = { ...venueVibesCache, ...next };
  notifyVenueVibesListeners();
}

function mapVenueSnapshotsToProfiles(venueSnapshots: QuerySnapshot<DocumentData>): Record<string, VenueProfileSnapshot> {
  const profiles: Record<string, VenueProfileSnapshot> = {};
  venueSnapshots.forEach(snapshot => {
    const data = snapshot.data();
    profiles[snapshot.id] = {
      name: firstString(data.name, data.displayName),
      username: firstString(data.username),
      logoUrl: firstString(data.logoUrl, data.venueLogoUrl, data.avatarUrl),
      coverUrl: firstString(data.coverUrl, data.coverImage, data.coverImageUrl),
      category: firstString(data.category),
      city: firstString(data.city),
      address: firstString(data.address),
      bio: firstString(data.bio),
      tags: Array.isArray(data.tags) ? data.tags : [],
      followersCount: typeof data.followersCount === 'number' ? data.followersCount : undefined,
      fallbackFollowersCount: typeof data.fallbackFollowersCount === 'number' ? data.fallbackFollowersCount : undefined,
      partnerId: firstString(data.partnerId) || undefined,
      ownerUid: firstString(data.ownerUid) || undefined,
      adminOnly: data.adminOnly === true,
      coordinates: data.coordinates && typeof data.coordinates.lat === 'number' ? data.coordinates : undefined,
    };
  });
  return profiles;
}

async function loadVenueVibesCache(force = false): Promise<void> {
  if (venueVibesLoadPromise && !force) return venueVibesLoadPromise;
  if (venueVibesCache.loaded && !force) return Promise.resolve();

  venueVibesLoadPromise = (async () => {
    setVenueVibesCache({ loading: true, error: null });
    try {
      const [loaded, venueSnapshots] = await Promise.all([
        readFirestoreActivities(),
        getDocs(collection(firestore, 'venues')),
      ]);
      setVenueVibesCache({ events: loaded, venueProfiles: mapVenueSnapshotsToProfiles(venueSnapshots), loaded: true, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setVenueVibesCache({ events: CURATED_EVENTS, venueProfiles: {}, loaded: true, error: message });
    } finally {
      setVenueVibesCache({ loading: false });
      venueVibesLoadPromise = null;
    }
  })();

  return venueVibesLoadPromise;
}

// One-shot resolution for a venue/organizer deep link (QR/share URL) — mirrors
// loadVenueVibesCache's own events+venues fetch and buildVenueSummaries/
// buildOrganizerSummaries call, without requiring useVenueVibes() to already
// be mounted somewhere and have finished loading (e.g. a cold-started share
// link, before VibeVenuesScreen/VenueProfileScreen/OrganizerProfileScreen
// have ever rendered). Deliberately bypasses the shared cache rather than
// reading from it, since a cold start has nothing cached yet; once the
// destination screen mounts normally afterward it populates the shared
// cache itself the usual way (see the module comment above
// VenueVibesCacheState for why duplicate fetches here are already
// tolerated).
export async function resolveVenueSummaryById(venueId: string): Promise<VenueSummary | null> {
  try {
    const [events, venueSnapshots] = await Promise.all([
      readFirestoreActivities(),
      getDocs(collection(firestore, 'venues')),
    ]);
    const venueProfiles = mapVenueSnapshotsToProfiles(venueSnapshots);
    // Same lookup order as VenueProfileScreen: event-derived venues first,
    // then partner venues from their venues/{id} doc (a partner with no
    // upcoming events is absent from the event-derived list).
    return buildVenueSummaries(events, venueProfiles).find(venue => venue.id === venueId)
      || buildPartnerVenueSummaries(events, venueProfiles).find(venue => venue.id === venueId)
      || null;
  } catch (error) {
    console.warn('[Tonyte][RN][VenueVibes] resolveVenueSummaryById failed', { venueId, error });
    return null;
  }
}

export async function resolveOrganizerSummaryById(organizerId: string): Promise<OrganizerSummary | null> {
  try {
    const events = await readFirestoreActivities();
    return buildOrganizerSummaries(events).find(organizer => organizer.id === organizerId) || null;
  } catch (error) {
    console.warn('[Tonyte][RN][VenueVibes] resolveOrganizerSummaryById failed', { organizerId, error });
    return null;
  }
}

export function useVenueVibes() {
  const [state, setState] = useState<VenueVibesCacheState>(() => venueVibesCache);

  useEffect(() => {
    venueVibesListeners.add(setState);
    setState(venueVibesCache);
    void loadVenueVibesCache();
    watchVenueProfiles();
    return () => {
      venueVibesListeners.delete(setState);
      stopWatchingVenueProfiles();
    };
  }, []);

  const refresh = useCallback(() => loadVenueVibesCache(true), []);

  const venues = useMemo(() => buildVenueSummaries(state.events, state.venueProfiles), [state.events, state.venueProfiles]);

  return { events: state.events, venues, venueProfiles: state.venueProfiles, loading: state.loading && !state.loaded, error: state.error, refresh };
}

export type PublisherVenue = { id: string; name: string; logoUrl?: string };

// The partner venue that published an event, for the "Published by" link on
// the event page. One small query instead of loading every venue. Only events
// from `activities` are trusted — its rules reserve partnerId for admins and
// pin ownerUid to the creator, so the link can't be spoofed. Admin-only
// (test) venues stay hidden from everyone else.
// Session cache so a feed full of one partner's events shares a single
// venues lookup instead of one query per card. Short TTL so logo/name edits
// still show up without an app restart.
const PUBLISHER_VENUE_TTL_MS = 5 * 60 * 1000;
const publisherVenueCache = new Map<string, { at: number; promise: Promise<{ id: string; data: DocumentData } | null> }>();
function lookupPublisherVenue(partnerId: string | undefined, ownerUid: string | undefined) {
  const key = partnerId ? `partner:${partnerId}` : `owner:${ownerUid}`;
  const cached = publisherVenueCache.get(key);
  if (cached && Date.now() - cached.at < PUBLISHER_VENUE_TTL_MS) return cached.promise;
  const venues = collection(firestore, 'venues');
  const lookup = partnerId
    ? query(venues, where('partnerId', '==', partnerId), limit(1))
    : query(venues, where('ownerUid', '==', ownerUid), limit(1));
  const promise = getDocs(lookup).then(snapshot => {
    const found = snapshot.docs[0];
    return found ? { id: found.id, data: found.data() } : null;
  });
  promise.catch(() => publisherVenueCache.delete(key));
  publisherVenueCache.set(key, { at: Date.now(), promise });
  return promise;
}

export function usePublisherVenue(event: TonyteActivity | null | undefined, viewerIsAdmin: boolean): PublisherVenue | null {
  const [venue, setVenue] = useState<PublisherVenue | null>(null);
  const trusted = Boolean(event) && (!event?.sourceCollection || event.sourceCollection === 'activities');
  const partnerId = trusted ? event?.partnerId : undefined;
  const ownerUid = trusted ? event?.ownerUid : undefined;

  useEffect(() => {
    if (!partnerId && !ownerUid) {
      setVenue(null);
      return undefined;
    }
    let active = true;
    lookupPublisherVenue(partnerId, ownerUid)
      .then(found => {
        if (!active) return;
        const data = found?.data;
        setVenue(found && data && (!data.adminOnly || viewerIsAdmin)
          ? { id: found.id, name: firstString(data.name, 'Tonyte Venue'), logoUrl: firstString(data.logoUrl) || undefined }
          : null);
      })
      .catch(error => console.warn('[Tonyte][RN][VenueVibes] publisher lookup failed', error));
    return () => { active = false; };
  }, [ownerUid, partnerId, viewerIsAdmin]);

  return venue;
}

export type FollowedPartner = { id: string; name: string; logoUrl?: string; category: string; city: string };

// The partners a user follows, from their own users/{uid}/followedVenues
// mirror (live), resolved against the current venues/{id} docs so names and
// pictures are always up to date. Venues that no longer exist — e.g. a
// disabled partner, whose doc the server moves out — simply drop out.
export function useFollowedPartners(userId: string | null) {
  const [ids, setIds] = useState<string[] | null>(null);
  const [partners, setPartners] = useState<FollowedPartner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setIds([]);
      return undefined;
    }
    return onSnapshot(
      collection(firestore, 'users', userId, 'followedVenues'),
      snapshot => setIds(snapshot.docs.map(item => item.id)),
      () => setIds([]),
    );
  }, [userId]);

  useEffect(() => {
    if (ids === null) return undefined;
    let active = true;
    setLoading(true);
    Promise.all(ids.map(id => getDoc(doc(firestore, 'venues', id)).catch(() => null)))
      .then(snapshots => {
        if (!active) return;
        setPartners(snapshots
          .filter((snap): snap is NonNullable<typeof snap> => Boolean(snap?.exists()))
          .map(snap => {
            const data = snap.data() ?? {};
            return {
              id: snap.id,
              name: firstString(data.name, 'Tonyte Venue'),
              logoUrl: firstString(data.logoUrl) || undefined,
              category: firstString(data.category),
              city: firstString(data.city),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name)));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ids]);

  return { partners, loading };
}

// How long an optimistic follow/unfollow is shown without the server count
// confirming it (e.g. someone else followed at the same moment) before the
// server value takes over again.
const OPTIMISTIC_FOLLOW_HOLD_MS = 15000;

// Follow / unfollow a venue. The button and the count flip on tap; the write
// runs in the background. venues/{id}.followersCount is recounted server-side
// (syncVenueFollowersCountEu) a few seconds later, so the tapped state is
// held until the server count matches it, and the number never steps back.
export function useVenueFollow(venue: VenueSummary | null, userId: string | null) {
  const { t } = useI18n();
  const venueId = venue?.id ?? null;
  const [serverFollowing, setServerFollowing] = useState(false);
  const [serverCount, setServerCount] = useState(Number(venue?.followersCount || 0));
  const [fallbackCount, setFallbackCount] = useState<number | null>(null);
  // The state the user tapped to, and the state/count it started from.
  const [optimistic, setOptimistic] = useState<{ following: boolean; baseFollowing: boolean; baseCount: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const desiredRef = useRef<boolean | null>(null);
  const syncingRef = useRef(false);
  const venueRef = useRef(venue);
  venueRef.current = venue;

  useEffect(() => {
    setServerCount(Number(venue?.followersCount || 0));
  }, [venue?.followersCount]);

  useEffect(() => {
    setOptimistic(null);
    desiredRef.current = null;
    setFallbackCount(null);
    if (!venueId) return undefined;
    const unsubVenue = onSnapshot(doc(firestore, 'venues', venueId), snap => {
      if (snap.exists()) setServerCount(Number(snap.data().followersCount || 0));
    }, () => undefined);
    let unsubFollow: (() => void) | undefined;
    if (userId) {
      unsubFollow = onSnapshot(doc(firestore, 'venues', venueId, 'followers', userId), snap => {
        setServerFollowing(snap.exists());
      }, () => setServerFollowing(false));
    } else {
      setServerFollowing(false);
    }
    return () => {
      unsubVenue();
      unsubFollow?.();
    };
  }, [userId, venueId]);

  // Venues whose followersCount was never set: count the followers directly.
  useEffect(() => {
    if (!venueId || !serverFollowing || serverCount > 0) {
      setFallbackCount(null);
      return undefined;
    }
    let cancelled = false;
    getCountFromServer(collection(firestore, 'venues', venueId, 'followers'))
      .then(snapshot => {
        if (!cancelled) setFallbackCount(snapshot.data().count || 0);
      })
      .catch(error => console.warn('[Tonyte][VenueFollow] followersCount fallback failed', { venueId, error }));
    return () => { cancelled = true; };
  }, [serverCount, serverFollowing, venueId]);

  const confirmedCount = fallbackCount ?? serverCount;
  const following = optimistic ? optimistic.following : serverFollowing;
  const optimisticCount = optimistic
    ? Math.max(0, optimistic.baseCount + (optimistic.following === optimistic.baseFollowing ? 0 : optimistic.following ? 1 : -1))
    : confirmedCount;

  // Hand back to the server once it shows the same thing, or after the hold.
  useEffect(() => {
    if (!optimistic || syncing) return undefined;
    if (serverFollowing === optimistic.following && confirmedCount === optimisticCount) {
      setOptimistic(null);
      return undefined;
    }
    const timer = setTimeout(() => setOptimistic(null), OPTIMISTIC_FOLLOW_HOLD_MS);
    return () => clearTimeout(timer);
  }, [confirmedCount, optimistic, optimisticCount, serverFollowing, syncing]);

  // Writes the latest tapped state; taps made while a write is running are
  // picked up by the next loop pass, so rapid taps end in the last state.
  const syncFollow = useCallback(async (targetVenue: VenueSummary, uid: string) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    const followerRef = doc(firestore, 'venues', targetVenue.id, 'followers', uid);
    const userFollowRef = doc(firestore, 'users', uid, 'followedVenues', targetVenue.id);
    try {
      while (desiredRef.current !== null && venueRef.current?.id === targetVenue.id) {
        const want = desiredRef.current;
        const [followerSnap, userFollowSnap] = await Promise.all([getDoc(followerRef), getDoc(userFollowRef)]);
        const batch = writeBatch(firestore);
        let writes = 0;
        if (want) {
          // Follow docs can't be updated (firestore.rules), only created.
          if (!followerSnap.exists()) { batch.set(followerRef, { userId: uid, followedAt: serverTimestamp() }); writes += 1; }
          if (!userFollowSnap.exists()) {
            batch.set(userFollowRef, {
              venueId: targetVenue.id,
              venueName: targetVenue.name,
              venueUsername: targetVenue.username,
              venueLogoUrl: targetVenue.logoUrl || '',
              followedAt: serverTimestamp(),
            });
            writes += 1;
          }
        } else {
          if (followerSnap.exists()) { batch.delete(followerRef); writes += 1; }
          if (userFollowSnap.exists()) { batch.delete(userFollowRef); writes += 1; }
        }
        // followersCount is recounted server-side; clients can't write it.
        if (writes) await batch.commit();
        if (desiredRef.current === want) desiredRef.current = null;
      }
    } catch (error) {
      console.warn('[Tonyte][VenueFollow] follow write failed', { venueId: targetVenue.id, userId: uid, error });
      desiredRef.current = null;
      setOptimistic(null);
      Alert.alert(t('common.error'), t('common.somethingWentWrong'));
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [t]);

  const toggleFollow = useCallback(() => {
    if (!venue) return;
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('venueProfiles.signInFollowVenue'));
      return;
    }
    // Owners can't follow their own venue (also enforced in firestore.rules).
    if (venue.ownerUid && venue.ownerUid === userId) return;
    assertSocialWriteAllowed();
    const next = !following;
    setOptimistic(current => (current
      ? { ...current, following: next }
      : { following: next, baseFollowing: serverFollowing, baseCount: confirmedCount }));
    desiredRef.current = next;
    syncFollow(venue, userId);
  }, [confirmedCount, following, serverFollowing, syncFollow, t, userId, venue]);

  return { following, followersCount: optimisticCount, toggleFollow };
}

export function useOrganizerFollow(organizer: OrganizerSummary | null, userId: string | null) {
  const { t } = useI18n();
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(organizer?.followersCount || 0);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setFollowersCount(organizer?.followersCount || 0);
  }, [organizer?.followersCount]);

  useEffect(() => {
    if (!organizer) return undefined;
    const organizerRef = doc(firestore, 'organizers', organizer.id);
    const unsubOrganizer = onSnapshot(organizerRef, snap => {
      if (snap.exists()) setFollowersCount(Number(snap.data().followersCount || 0));
    }, () => undefined);
    let unsubFollow: (() => void) | undefined;
    if (userId) {
      unsubFollow = onSnapshot(doc(firestore, 'organizers', organizer.id, 'followers', userId), snap => {
        setFollowing(snap.exists());
      }, () => setFollowing(false));
    } else {
      setFollowing(false);
    }
    return () => {
      unsubOrganizer();
      unsubFollow?.();
    };
  }, [organizer, t, userId]);

  const toggleFollow = useCallback(async () => {
    if (!organizer) return;
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('venueProfiles.signInFollowOrganizer'));
      return;
    }
    assertSocialWriteAllowed();
    setPending(true);
    const organizerRef = doc(firestore, 'organizers', organizer.id);
    const followerRef = doc(firestore, 'organizers', organizer.id, 'followers', userId);
    const userFollowRef = doc(firestore, 'users', userId, 'followedOrganizers', organizer.id);
    try {
      const existing = await getDoc(followerRef);
      if (existing.exists()) {
        setFollowersCount(count => Math.max(0, count - 1));
        await Promise.all([
          deleteDoc(followerRef),
          deleteDoc(userFollowRef),
          setDoc(organizerRef, { followersCount: increment(-1) }, { merge: true }).catch(() => undefined),
        ]);
      } else {
        setFollowersCount(count => count + 1);
        await Promise.all([
          setDoc(followerRef, { userId, followedAt: serverTimestamp() }),
          setDoc(userFollowRef, {
            organizerId: organizer.id,
            organizerName: organizer.displayName,
            organizerUsername: organizer.username,
            organizerLogoUrl: organizer.avatarUrl || '',
            followedAt: serverTimestamp(),
          }),
          setDoc(organizerRef, { followersCount: increment(1) }, { merge: true }).catch(() => undefined),
        ]);
      }
    } finally {
      setPending(false);
    }
  }, [organizer, t, userId]);

  return { following, followersCount, pending, toggleFollow };
}

// Server-maintained interested / event-chat member counts
// (eventStats/{eventId}, kept in sync by Cloud Functions). A single-doc
// listener per tile is cheap and keeps the grid live as people join.
export function useEventPublicStats(eventId: string) {
  const [stats, setStats] = useState({ interestedCount: 0, chatMembersCount: 0 });
  useEffect(() => {
    setStats({ interestedCount: 0, chatMembersCount: 0 });
    if (!eventId) return undefined;
    return onSnapshot(doc(firestore, 'eventStats', eventId), snap => {
      const data = snap.data();
      setStats({
        interestedCount: Number(data?.interestedCount || 0),
        chatMembersCount: Number(data?.chatMembersCount || 0),
      });
    }, () => undefined);
  }, [eventId]);
  return stats;
}

// `realtime: false` (used for grid/tile previews, e.g. an organizer's post
// grid with dozens of tiles) fetches counts once via getCountFromServer
// instead of opening 2 onSnapshot listeners per tile that would otherwise
// download every like/comment document just to read `.size`. Interactive
// views (the actual like/comment button) keep realtime: true so a tap is
// reflected immediately.
export function useVenuePostStats(eventId: string, userId: string | null, realtime: boolean = true) {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);

  useEffect(() => {
    if (!eventId) return undefined;

    if (!realtime) {
      let cancelled = false;
      Promise.all([
        getCountFromServer(collection(firestore, 'events', eventId, 'likes')),
        getCountFromServer(collection(firestore, 'events', eventId, 'comments')),
      ]).then(([likesSnap, commentsSnap]) => {
        if (cancelled) return;
        setLikesCount(likesSnap.data().count);
        setCommentsCount(commentsSnap.data().count);
      }).catch(() => {
        if (!cancelled) {
          setLikesCount(0);
          setCommentsCount(0);
        }
      });
      return () => { cancelled = true; };
    }

    const unsubLikes = onSnapshot(collection(firestore, 'events', eventId, 'likes'), snap => {
      setLikesCount(snap.size);
    }, () => {
      setLikesCount(0);
    });
    const unsubComments = onSnapshot(collection(firestore, 'events', eventId, 'comments'), snap => {
      setCommentsCount(snap.size);
    }, () => {
      setCommentsCount(0);
    });
    return () => {
      unsubLikes();
      unsubComments();
    };
  }, [eventId, realtime]);

  useEffect(() => {
    if (!eventId || !userId) {
      setLiked(false);
      return undefined;
    }
    const unsubLike = onSnapshot(doc(firestore, 'events', eventId, 'likes', userId), snap => setLiked(snap.exists()), () => setLiked(false));
    return unsubLike;
  }, [eventId, userId]);

  return { liked, likesCount, commentsCount };
}

export function useVenuePostInteractions(event: TonyteActivity | null, userId: string | null) {
  const { t } = useI18n();
  const eventId = event ? getResolvedEventId(event) : '';
  const [pending, setPending] = useState(false);
  const { liked, likesCount, commentsCount } = useVenuePostStats(eventId, userId);
  const sharesCount = event?.sharesCount || 0;

  const toggleLike = useCallback(async () => {
    if (!event || !eventId) return;
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('venueProfiles.signInLike'));
      return;
    }
    assertSocialWriteAllowed();
    setPending(true);
    const likeRef = doc(firestore, 'events', eventId, 'likes', userId);
    try {
      const existing = await getDoc(likeRef);
      if (existing.exists()) {
        await deleteDoc(likeRef);
      } else {
        await setDoc(likeRef, { userId, createdAt: serverTimestamp() });
      }
    } finally {
      setPending(false);
    }
  }, [event, eventId, t, userId]);

  const addComment = useCallback(async (text: string) => {
    if (!event || !eventId) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('venueProfiles.signInComment'));
      return;
    }
    assertSocialWriteAllowed();
    const currentUser = await loadCurrentTonyteUser(userId);
    await addDoc(collection(firestore, 'events', eventId, 'comments'), {
      userId,
      userName: currentUser.name,
      userPhotoUrl: currentUser.photo,
      text: trimmed,
      createdAt: serverTimestamp(),
    });
  }, [event, eventId, t, userId]);

  const deleteComment = useCallback(async (commentId: string, commentUserId?: string) => {
    if (!eventId || !commentId) return;
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('venueProfiles.signInDeleteComment'));
      return;
    }
    if (commentUserId && commentUserId !== userId) {
      Alert.alert(t('venueProfiles.notAllowed'), t('venueProfiles.ownCommentsOnly'));
      return;
    }
    await deleteDoc(doc(firestore, 'events', eventId, 'comments', commentId));
  }, [eventId, t, userId]);

  const loadComments = useCallback(async () => {
    if (!eventId) return [];
    const snap = await getDocs(query(collection(firestore, 'events', eventId, 'comments'), orderBy('createdAt', 'desc')));
    return snap.docs.map(item => ({ id: item.id, ...(item.data() as any) }));
  }, [eventId]);

  const sharePost = useCallback(async () => {
    if (!event || !eventId) return;
    const link = buildTonyteEventLink(eventId);
    await shareEventExternally(event, {
      title: event.title,
      message: [t('common.sharedOnTonyte').replace('{name}', event.title), link, event.venueName || event.locationName || event.city].filter(Boolean).join('\n'),
      url: link,
    });
  }, [event, eventId, t]);

  return { liked, likesCount, commentsCount, sharesCount, pending, toggleLike, addComment, deleteComment, loadComments, sharePost };
}

export async function shareVenueProfile(venue: VenueSummary) {
  const link = buildTonyteVenueLink(venue.id);
  await Share.share({
    title: venue.name,
    message: [translateCurrent('common.sharedOnTonyte').replace('{name}', venue.name), link, venue.city].filter(Boolean).join('\n'),
    url: link,
  });
}

export async function shareOrganizerProfile(organizer: OrganizerSummary) {
  const link = buildTonyteOrganizerLink(organizer.id);
  await Share.share({
    title: organizer.displayName,
    message: [translateCurrent('common.sharedOnTonyte').replace('{name}', organizer.displayName), link, organizer.city].filter(Boolean).join('\n'),
    url: link,
  });
}

export function splitVenueEvents(events: TonyteActivity[]) {
  const upcoming: TonyteActivity[] = [];
  const past: TonyteActivity[] = [];
  events.forEach(event => (isActivityCurrentOrUpcoming(event) ? upcoming : past).push(event));
  return {
    upcoming: upcoming.sort(sortActivitiesByStart),
    past: past.sort((a, b) => {
      const aTime = parseActivityDate(a).startAt?.getTime() || 0;
      const bTime = parseActivityDate(b).startAt?.getTime() || 0;
      return bTime - aTime;
    }),
  };
}

export const getCurrentVenueUserId = () => auth.currentUser?.uid || null;
